import React, { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy,
  Clock,
  Bomb,
  Swords,
  Check,
  Sparkles,
  Flame,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { API } from "@/lib/api";
import {
  getClientFingerprint,
  isAlreadyParticipatedError,
  alreadyParticipatedMessage,
  extractErrorMessage,
} from "@/lib/fingerprint";

// Default product emojis used when admin hasn't uploaded custom images.
const DEFAULT_EMOJIS = ["🍎", "🍊", "🍌", "🍇", "🍓", "🥝", "🍉", "🍑", "🍋", "🥭"];
const DEFAULT_BOMB_EMOJI = "💣";

// Physics / tuning constants.
const GRAVITY = 900; // px/s²
const SPAWN_INTERVAL_MS_MIN = 650;
const SPAWN_INTERVAL_MS_MAX = 1100;
const BOMB_PROBABILITY = 0.18; // 18% chance the spawn is a bomb
const PROJECTILE_RADIUS = 44; // hit area radius
const TRAIL_DURATION_MS = 220;
const COMBO_WINDOW_MS = 180; // max delay between slices in one combo
const COMBO_MAX_MULT = 3;

function now() {
  return performance.now();
}

/**
 * NinjaGame — canvas-based fruit-ninja style mini-game, customised with the
 * merchant's products as projectiles. One run per participant, best score
 * decides the winners when admin draws.
 */
export default function NinjaGame({
  game,
  participant,
  ctasCompleted,
  receipt,
  onAlreadyParticipated,
  onSubmitted,
}) {
  const { t } = useTranslation();
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  // Game state (ref-based for animation loop, shadowed by React state for UI)
  const stateRef = useRef({
    projectiles: [], // {id, kind, x, y, vx, vy, emoji?, imgKey?, rot, vrot, radius, sliced}
    pieces: [], // Half pieces after a slice — each piece clip-drawn through a cut line
    particles: [], // Juice splash + flash particles
    trail: [], // {x, y, t}
    combo: [], // [{t}] — recent slices for combo detection
    lastSpawnAt: 0,
    startedAt: 0,
    running: false,
    endedBy: null, // "timeout" | "bomb"
    // Live counters — kept in the ref to avoid stale-closure reads at
    // submission time (React state updates are async + our closures inside
    // useEffect don't refresh every slice).
    score: 0,
    slicesCount: 0,
  });

  const [score, setScore] = useState(0);
  const [slicesCount, setSlicesCount] = useState(0);
  const [timeLeft, setTimeLeft] = useState(game.ninja_duration || 45);
  const [status, setStatus] = useState("intro"); // intro | countdown | playing | finished | submitted
  const [countdown, setCountdown] = useState(3);
  const [comboMult, setComboMult] = useState(1);
  const [leaderboard, setLeaderboard] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // response payload

  const duration = game.ninja_duration || 45;
  const pointsPerProduct = game.ninja_points_per_product || 10;
  const bladeColor = game.ninja_blade_color || "#a855f7";

  // -----------------------------------------------------------------
  // Load images (custom product / bomb) as HTMLImageElement once.
  // -----------------------------------------------------------------
  const imagesRef = useRef({ products: [], bomb: null });
  useEffect(() => {
    const productsUrls = game.ninja_product_images || [];
    const bombUrl = game.ninja_bomb_image || null;
    const loadImg = (url) =>
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
      });
    (async () => {
      const products = (
        await Promise.all(productsUrls.map(loadImg))
      ).filter(Boolean);
      const bomb = bombUrl ? await loadImg(bombUrl) : null;
      imagesRef.current = { products, bomb };
    })();
  }, [game.ninja_product_images, game.ninja_bomb_image]);

  // -----------------------------------------------------------------
  // Leaderboard polling.
  // -----------------------------------------------------------------
  const fetchLeaderboard = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/public/games/${game.slug}/ninja-leaderboard?limit=3`
      );
      setLeaderboard(data.leaderboard || []);
    } catch {
      /* silent */
    }
  }, [game.slug]);

  useEffect(() => {
    fetchLeaderboard();
    const id = setInterval(fetchLeaderboard, 5000);
    return () => clearInterval(id);
  }, [fetchLeaderboard]);

  // -----------------------------------------------------------------
  // Canvas sizing.
  // -----------------------------------------------------------------
  const sizeRef = useRef({ w: 360, h: 520, dpr: 1 });
  useEffect(() => {
    function resize() {
      const c = canvasRef.current;
      if (!c) return;
      const parent = containerRef.current;
      const rect = parent.getBoundingClientRect();
      const w = Math.floor(rect.width);
      const h = Math.max(420, Math.min(640, Math.floor(window.innerHeight * 0.62)));
      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      c.width = w * dpr;
      c.height = h * dpr;
      c.style.width = `${w}px`;
      c.style.height = `${h}px`;
      sizeRef.current = { w, h, dpr };
    }
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  // -----------------------------------------------------------------
  // Main animation loop.
  // -----------------------------------------------------------------
  useEffect(() => {
    let rafId;
    let lastT = now();

    function spawn() {
      const { w, h } = sizeRef.current;
      const st = stateRef.current;
      const isBomb = Math.random() < BOMB_PROBABILITY;
      const fromLeft = Math.random() < 0.5;
      const x = fromLeft
        ? Math.random() * w * 0.35 + 30
        : w - (Math.random() * w * 0.35 + 30);
      // Target a landing zone toward the opposite side of the canvas.
      const targetX = fromLeft
        ? w * (0.55 + Math.random() * 0.4)
        : w * (0.05 + Math.random() * 0.4);
      const peakY = h * (0.05 + Math.random() * 0.15); // how high it will arc
      // Back-calculate velocities so it arcs from (x, h) to peakY then down.
      // vy upward, apex reached when vy=0 → h - (vy²/(2g))
      const apexDy = h - peakY;
      const vy = -Math.sqrt(2 * GRAVITY * apexDy);
      const tApex = Math.abs(vy) / GRAVITY;
      const tTotal = tApex * 2 * 0.85; // back slightly before full down
      const vx = (targetX - x) / tTotal;

      const imgs = imagesRef.current;
      const useImg = !isBomb && imgs.products.length > 0;
      const useBombImg = isBomb && imgs.bomb;

      st.projectiles.push({
        id: Math.random().toString(36).slice(2),
        kind: isBomb ? "bomb" : "product",
        x,
        y: h + PROJECTILE_RADIUS,
        vx,
        vy,
        rot: Math.random() * Math.PI * 2,
        vrot: (Math.random() - 0.5) * 6,
        radius: PROJECTILE_RADIUS,
        sliced: false,
        img: useImg
          ? imgs.products[
              Math.floor(Math.random() * imgs.products.length)
            ]
          : useBombImg
            ? imgs.bomb
            : null,
        emoji: isBomb
          ? DEFAULT_BOMB_EMOJI
          : DEFAULT_EMOJIS[Math.floor(Math.random() * DEFAULT_EMOJIS.length)],
      });
    }

    function step(t) {
      rafId = requestAnimationFrame(step);
      const dtMs = Math.min(50, t - lastT);
      const dt = dtMs / 1000;
      lastT = t;

      const c = canvasRef.current;
      if (!c) return;
      const ctx = c.getContext("2d");
      const { w, h, dpr } = sizeRef.current;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Clear with a semi-transparent overlay for subtle trails in bg.
      ctx.fillStyle = "rgba(15, 12, 36, 0.24)";
      ctx.fillRect(0, 0, w, h);

      const st = stateRef.current;
      if (!st.running) {
        // Paint projectiles + remaining pieces/particles one last time
        // (e.g. post-game freeze to show final slice effects briefly).
        drawProjectiles(ctx, st.projectiles);
        drawPieces(ctx, st.pieces, t);
        drawParticles(ctx, st.particles, t);
        drawTrail(ctx, st.trail);
        return;
      }

      // Spawn logic
      if (t - st.lastSpawnAt > randBetween(SPAWN_INTERVAL_MS_MIN, SPAWN_INTERVAL_MS_MAX)) {
        spawn();
        st.lastSpawnAt = t;
      }

      // Update projectiles
      const next = [];
      for (const p of st.projectiles) {
        if (p.sliced) continue; // sliced ones are kept as pieces instead
        p.x += p.vx * dt;
        p.y += p.vy * dt;
        p.vy += GRAVITY * dt;
        p.rot += p.vrot * dt;
        // Kill if off-screen (bottom)
        if (p.y - p.radius > h + 60) continue;
        next.push(p);
      }
      st.projectiles = next;

      // Update pieces (slice halves)
      const nowT = t;
      const keptPieces = [];
      for (const pc of st.pieces) {
        const age = nowT - pc.born;
        if (age > pc.life || pc.y - pc.radius > h + 80) continue;
        pc.x += pc.vx * dt;
        pc.y += pc.vy * dt;
        pc.vy += GRAVITY * dt;
        pc.rot += pc.vrot * dt;
        keptPieces.push(pc);
      }
      st.pieces = keptPieces;

      // Update particles (juice + flash)
      const keptParticles = [];
      for (const pa of st.particles) {
        const age = nowT - pa.born;
        if (age > pa.life) continue;
        pa.x += pa.vx * dt;
        pa.y += pa.vy * dt;
        pa.vy += GRAVITY * 0.55 * dt;
        keptParticles.push(pa);
      }
      st.particles = keptParticles;

      // Expire trail points
      st.trail = st.trail.filter((pt) => t - pt.t < TRAIL_DURATION_MS);

      // Draw
      drawProjectiles(ctx, st.projectiles);
      drawPieces(ctx, st.pieces, nowT);
      drawParticles(ctx, st.particles, nowT);
      drawTrail(ctx, st.trail);

      // Time remaining
      const elapsedMs = t - st.startedAt;
      const remain = Math.max(0, duration - Math.floor(elapsedMs / 1000));
      setTimeLeft(remain);
      if (elapsedMs >= duration * 1000) {
        endGame("timeout");
      }
    }

    function drawProjectiles(ctx, list) {
      for (const p of list) {
        ctx.save();
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rot);
        if (p.img) {
          const sz = p.radius * 2.2;
          ctx.drawImage(p.img, -sz / 2, -sz / 2, sz, sz);
          if (p.kind === "bomb") {
            // Subtle red glow around custom bomb images too
            ctx.strokeStyle = "rgba(239,68,68,0.9)";
            ctx.lineWidth = 3;
            ctx.beginPath();
            ctx.arc(0, 0, p.radius + 4, 0, Math.PI * 2);
            ctx.stroke();
          }
        } else {
          // Emoji fallback
          const sz = p.radius * 1.7;
          ctx.font = `${sz}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          if (p.kind === "bomb") {
            ctx.shadowColor = "rgba(239,68,68,0.85)";
            ctx.shadowBlur = 18;
          }
          ctx.fillText(p.emoji, 0, 0);
        }
        ctx.restore();
      }
    }

    // Draw a half-piece of a sliced projectile, clipped to one side of the
    // cut line (local frame). Each piece keeps its own rotation/spin.
    function drawPieces(ctx, list, nowT) {
      for (const pc of list) {
        const age = nowT - pc.born;
        const fade = Math.max(0, 1 - age / pc.life);
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(pc.x, pc.y);
        ctx.rotate(pc.rot);
        // Clip to one side of the slice line (which is along pc.sliceAngleLocal
        // in the piece's local frame).
        ctx.rotate(pc.sliceAngleLocal);
        ctx.beginPath();
        const clipH = pc.radius * 2.4;
        const clipW = pc.radius * 2.8;
        if (pc.side === 0) {
          ctx.rect(-clipW / 2, -clipH, clipW, clipH);
        } else {
          ctx.rect(-clipW / 2, 0, clipW, clipH);
        }
        ctx.clip();
        ctx.rotate(-pc.sliceAngleLocal);
        // Draw the projectile visual
        if (pc.img) {
          const sz = pc.radius * 2.2;
          ctx.drawImage(pc.img, -sz / 2, -sz / 2, sz, sz);
        } else {
          const sz = pc.radius * 1.7;
          ctx.font = `${sz}px "Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji",sans-serif`;
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(pc.emoji, 0, 0);
        }
        // Highlight the cut edge (glossy inner line)
        ctx.rotate(pc.sliceAngleLocal);
        ctx.strokeStyle = "rgba(255,255,255,0.9)";
        ctx.lineCap = "round";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(-pc.radius * 0.9, 0);
        ctx.lineTo(pc.radius * 0.9, 0);
        ctx.stroke();
        ctx.restore();
      }
    }

    // Draw juice splash particles + impact flashes.
    function drawParticles(ctx, list, nowT) {
      for (const pa of list) {
        const age = nowT - pa.born;
        const frac = Math.max(0, 1 - age / pa.life);
        if (pa.flash) {
          ctx.save();
          ctx.globalAlpha = frac * 0.9;
          const grad = ctx.createRadialGradient(pa.x, pa.y, 0, pa.x, pa.y, pa.r * (1 + (1 - frac)));
          grad.addColorStop(0, "rgba(255,255,255,1)");
          grad.addColorStop(0.5, "rgba(255,255,255,0.6)");
          grad.addColorStop(1, "rgba(255,255,255,0)");
          ctx.fillStyle = grad;
          ctx.beginPath();
          ctx.arc(pa.x, pa.y, pa.r * (1 + (1 - frac) * 1.4), 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        } else {
          ctx.save();
          ctx.globalAlpha = frac;
          ctx.fillStyle = pa.color;
          ctx.beginPath();
          ctx.arc(pa.x, pa.y, pa.r, 0, Math.PI * 2);
          ctx.fill();
          ctx.restore();
        }
      }
    }

    function drawTrail(ctx, trail) {
      if (trail.length < 2) return;
      // Draw glow trail
      ctx.save();
      for (let pass = 0; pass < 2; pass++) {
        ctx.strokeStyle = bladeColor;
        ctx.shadowColor = bladeColor;
        ctx.shadowBlur = pass === 0 ? 22 : 10;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        for (let i = 0; i < trail.length; i++) {
          const pt = trail[i];
          const age = (now() - pt.t) / TRAIL_DURATION_MS;
          const w = Math.max(1, 10 * (1 - age));
          if (i === 0) {
            ctx.moveTo(pt.x, pt.y);
          } else {
            ctx.lineTo(pt.x, pt.y);
          }
          ctx.lineWidth = w;
        }
        ctx.stroke();
      }
      ctx.restore();
    }

    function randBetween(a, b) {
      return a + Math.random() * (b - a);
    }

    rafId = requestAnimationFrame(step);
    return () => cancelAnimationFrame(rafId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bladeColor, duration]);

  // -----------------------------------------------------------------
  // Pointer (swipe) handling.
  // -----------------------------------------------------------------
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    let pointerDown = false;
    const st = stateRef.current;

    function getPos(e) {
      const r = c.getBoundingClientRect();
      const pt = e.touches ? e.touches[0] : e;
      return { x: pt.clientX - r.left, y: pt.clientY - r.top };
    }

    function addPoint(e) {
      if (!st.running) return;
      const pos = getPos(e);
      const t = now();
      st.trail.push({ x: pos.x, y: pos.y, t });
      if (st.trail.length > 40) st.trail.shift();
      // Check collisions against last 2 trail points (segment)
      if (st.trail.length >= 2) {
        const a = st.trail[st.trail.length - 2];
        const b = st.trail[st.trail.length - 1];
        for (const p of st.projectiles) {
          if (p.sliced) continue;
          if (pointToSegmentDist(p.x, p.y, a.x, a.y, b.x, b.y) < p.radius) {
            onSlice(p, t, b.x - a.x, b.y - a.y);
          }
        }
      }
    }

    function onDown(e) {
      if (!st.running) return;
      e.preventDefault();
      pointerDown = true;
      st.trail = [];
      addPoint(e);
    }
    function onMove(e) {
      if (!pointerDown) return;
      e.preventDefault();
      addPoint(e);
    }
    function onUp() {
      pointerDown = false;
    }

    c.addEventListener("pointerdown", onDown);
    c.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    c.addEventListener("touchstart", onDown, { passive: false });
    c.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);

    return () => {
      c.removeEventListener("pointerdown", onDown);
      c.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      c.removeEventListener("touchstart", onDown);
      c.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  function pointToSegmentDist(px, py, x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) {
      return Math.hypot(px - x1, py - y1);
    }
    let t = ((px - x1) * dx + (py - y1) * dy) / lenSq;
    t = Math.max(0, Math.min(1, t));
    const cx = x1 + t * dx;
    const cy = y1 + t * dy;
    return Math.hypot(px - cx, py - cy);
  }

  // Map a default emoji to its splash/juice color so slicing feels visually
  // coherent. Custom images fall back to the blade color.
  function juiceColorFor(p) {
    if (p.kind === "bomb") return "#4b5563"; // smoke-gray
    const map = {
      "🍎": "#ef4444",
      "🍊": "#f97316",
      "🍌": "#fde047",
      "🍇": "#a855f7",
      "🍓": "#f43f5e",
      "🥝": "#84cc16",
      "🍉": "#f87171",
      "🍑": "#fb923c",
      "🍋": "#fde047",
      "🥭": "#f59e0b",
    };
    return map[p.emoji] || bladeColor;
  }

  function spawnSliceEffect(p, swipeDx, swipeDy) {
    const st = stateRef.current;
    // Swipe direction (global frame); fallback to random if too short.
    const len = Math.hypot(swipeDx || 0, swipeDy || 0);
    const ang =
      len > 0.5
        ? Math.atan2(swipeDy, swipeDx)
        : Math.random() * Math.PI * 2;
    // Perpendicular to swipe — pieces fly apart along this axis
    const perpX = -Math.sin(ang);
    const perpY = Math.cos(ang);
    const push = 160 + Math.random() * 80;
    // Slice angle in each piece's local frame (piece inherits p.rot)
    const localA = ang - p.rot;
    const bornT = now();

    for (let side = 0; side < 2; side++) {
      const dir = side === 0 ? 1 : -1;
      st.pieces.push({
        x: p.x,
        y: p.y,
        vx: p.vx + perpX * push * dir,
        vy: p.vy + perpY * push * dir - 40,
        rot: p.rot,
        vrot: p.vrot + dir * (3 + Math.random() * 2),
        img: p.img,
        emoji: p.emoji,
        kind: p.kind,
        radius: p.radius,
        sliceAngleLocal: localA,
        side,
        born: bornT,
        life: 750,
      });
    }

    // Juice particles
    const color = juiceColorFor(p);
    const count = 10 + Math.floor(Math.random() * 6);
    for (let i = 0; i < count; i++) {
      const a2 = Math.random() * Math.PI * 2;
      const speed = 90 + Math.random() * 220;
      st.particles.push({
        x: p.x,
        y: p.y,
        vx: Math.cos(a2) * speed,
        vy: Math.sin(a2) * speed,
        r: 2.5 + Math.random() * 4.5,
        color,
        born: bornT,
        life: 450 + Math.random() * 350,
      });
    }
    // Impact flash
    st.particles.push({
      x: p.x,
      y: p.y,
      vx: 0,
      vy: 0,
      r: p.radius * 0.9,
      color: "rgba(255,255,255,0.95)",
      born: bornT,
      life: 180,
      flash: true,
    });
  }

  function onSlice(p, t, swipeDx = 0, swipeDy = 0) {
    p.sliced = true;
    const st = stateRef.current;
    if (p.kind === "bomb") {
      // Show the bomb explosion visual before ending
      spawnSliceEffect(p, swipeDx, swipeDy);
      endGame("bomb");
      return;
    }
    spawnSliceEffect(p, swipeDx, swipeDy);
    // Clean combo window
    st.combo = st.combo.filter((c) => t - c.t < COMBO_WINDOW_MS);
    st.combo.push({ t });
    const mult = Math.min(COMBO_MAX_MULT, st.combo.length);
    const pts = pointsPerProduct * mult;
    // Write-through: ref is source of truth, React state follows for UI.
    st.score += pts;
    st.slicesCount += 1;
    setScore(st.score);
    setSlicesCount(st.slicesCount);
    setComboMult(mult);
    // Reset mult display after a short delay (visual only)
    setTimeout(() => setComboMult(1), 700);
  }

  // -----------------------------------------------------------------
  // Start / end.
  // -----------------------------------------------------------------
  function startCountdown() {
    setStatus("countdown");
    setCountdown(3);
    let n = 3;
    const id = setInterval(() => {
      n -= 1;
      if (n <= 0) {
        clearInterval(id);
        startGame();
      } else {
        setCountdown(n);
      }
    }, 800);
  }

  function startGame() {
    const st = stateRef.current;
    st.projectiles = [];
    st.pieces = [];
    st.particles = [];
    st.trail = [];
    st.combo = [];
    st.score = 0;
    st.slicesCount = 0;
    st.submittedFlag = false;
    st.lastSpawnAt = now();
    st.startedAt = now();
    st.running = true;
    st.endedBy = null;
    setScore(0);
    setSlicesCount(0);
    setTimeLeft(duration);
    setStatus("playing");
  }

  function endGame(reason) {
    const st = stateRef.current;
    if (!st.running) return;
    st.running = false;
    st.endedBy = reason;
    setStatus("finished");
    // Slight delay before autosubmit for dramatic reveal.
    setTimeout(() => submitScore(reason), 400);
  }

  async function submitScore(reason) {
    const st = stateRef.current;
    // Guard against double-submit via a ref flag; React state is stale inside
    // the setTimeout closure that invokes us from endGame.
    if (st.submittedFlag) return;
    st.submittedFlag = true;
    setSubmitting(true);
    try {
      const elapsed = Math.max(0, now() - st.startedAt);
      // Read from the ref — React state closures here were captured at the
      // time submitScore/endGame were defined (render 1) and would read 0.
      const finalScore = st.score;
      const finalSlices = st.slicesCount;
      const { data } = await axios.post(
        `${API}/public/games/${game.slug}/ninja-submit`,
        {
          participant,
          ctas_completed: ctasCompleted || [],
          score: finalScore,
          slices_count: finalSlices,
          time_taken_ms: Math.floor(elapsed),
          ended_by: reason === "bomb" ? "bomb" : "timeout",
          fingerprint: getClientFingerprint(),
          receipt_url: receipt?.url || null,
          receipt_ocr: receipt?.ocr || null,
        }
      );
      setSubmitted(data);
      setStatus("submitted");
      onSubmitted?.();
      fetchLeaderboard();
      if (!data.already_entered) {
        toast.success(t("play.ninja_submitted"));
      } else {
        toast.info(t("play.ninja_already_played"));
      }
    } catch (e) {
      if (isAlreadyParticipatedError(e)) {
        onAlreadyParticipated?.(alreadyParticipatedMessage(e));
        return;
      }
      toast.error(extractErrorMessage(e, "Error"));
      setStatus("finished");
      // Allow retry if the submit failed (e.g., network blip)
      st.submittedFlag = false;
    } finally {
      setSubmitting(false);
    }
  }

  // -----------------------------------------------------------------
  // Render.
  // -----------------------------------------------------------------
  return (
    <div
      className="max-w-xl mx-auto space-y-3"
      data-testid="ninja-game"
    >
      {/* Top HUD */}
      <div className="flex items-center justify-between gap-2">
        <div className="qrise-card px-3 py-2 flex items-center gap-1.5">
          <Clock className="h-4 w-4 text-violet-500" />
          <span
            className="text-sm font-bold tabular-nums"
            data-testid="ninja-timer"
          >
            {timeLeft}s
          </span>
        </div>
        <div className="qrise-card px-3 py-2 flex items-center gap-1.5">
          <Swords className="h-4 w-4 text-emerald-500" />
          <span className="text-sm font-bold tabular-nums" data-testid="ninja-score">
            {score}
          </span>
          {comboMult > 1 && status === "playing" && (
            <span
              className="ml-1.5 inline-flex items-center gap-0.5 rounded-full bg-amber-400/20 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 text-[10px] font-extrabold animate-pulse"
              data-testid="ninja-combo"
            >
              <Flame className="h-3 w-3" />x{comboMult}
            </span>
          )}
        </div>
      </div>

      {/* Game arena */}
      <div
        ref={containerRef}
        className="relative rounded-2xl overflow-hidden border border-border"
        style={{
          background:
            "linear-gradient(180deg, #120b2a 0%, #1d1450 45%, #2a1a6b 100%)",
          touchAction: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          className="block w-full"
          data-testid="ninja-canvas"
        />

        {/* Merchant logo watermark */}
        {game.merchant_logo && status === "intro" && (
          <img
            src={game.merchant_logo}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute opacity-10 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-48 w-48 object-contain"
          />
        )}

        {/* Intro overlay */}
        {status === "intro" && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center text-white text-center p-6"
            data-testid="ninja-intro"
          >
            <div className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.2em] mb-3 backdrop-blur-sm">
              <Swords className="h-3 w-3" />
              {t("play.ninja_badge")}
            </div>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-2">
              {t("play.ninja_title")}
            </h3>
            <p className="text-sm text-white/70 max-w-sm">
              {t("play.ninja_subtitle", {
                duration,
                points: pointsPerProduct,
              })}
            </p>
            <div className="mt-4 flex items-center gap-3 text-xs text-white/70">
              <div className="inline-flex items-center gap-1">
                <Bomb className="h-3.5 w-3.5 text-rose-400" />
                {t("play.ninja_avoid_bombs")}
              </div>
              <div className="inline-flex items-center gap-1">
                <Flame className="h-3.5 w-3.5 text-amber-400" />
                {t("play.ninja_combo_hint")}
              </div>
            </div>
            <Button
              type="button"
              onClick={startCountdown}
              className="mt-6 h-12 px-8 rounded-full text-sm font-extrabold uppercase tracking-[0.22em]"
              style={{
                background:
                  "linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)",
                color: "white",
                boxShadow:
                  "0 12px 32px -10px rgba(139,92,246,0.65), inset 0 1px 0 rgba(255,255,255,0.3)",
              }}
              data-testid="ninja-start-btn"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              {t("play.ninja_start")}
            </Button>
          </div>
        )}

        {/* Countdown overlay */}
        {status === "countdown" && (
          <div
            className="absolute inset-0 flex items-center justify-center text-white"
            data-testid="ninja-countdown"
          >
            <div
              className="text-8xl font-extrabold tracking-tight"
              style={{
                textShadow: "0 6px 40px rgba(139,92,246,0.7)",
              }}
            >
              {countdown}
            </div>
          </div>
        )}

        {/* Finished (brief, before result overlay) */}
        {status === "finished" && (
          <div
            className="absolute inset-0 flex items-center justify-center text-white text-center p-6"
            data-testid="ninja-finished"
          >
            <div>
              <div
                className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-[0.22em] mb-3 ${
                  stateRef.current.endedBy === "bomb"
                    ? "bg-rose-500/20 text-rose-300"
                    : "bg-violet-500/20 text-violet-200"
                }`}
              >
                {stateRef.current.endedBy === "bomb" ? (
                  <>
                    <Bomb className="h-3 w-3" />
                    {t("play.ninja_bomb_hit")}
                  </>
                ) : (
                  <>
                    <Clock className="h-3 w-3" />
                    {t("play.ninja_time_up")}
                  </>
                )}
              </div>
              <div className="text-5xl font-extrabold mb-1 tabular-nums">
                {score}
              </div>
              <div className="text-xs text-white/60 uppercase tracking-[0.2em]">
                {t("play.ninja_points")}
              </div>
              {submitting && (
                <div className="mt-3 text-xs text-white/70">
                  {t("common.loading")}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Submitted result */}
        {status === "submitted" && submitted && (
          <div
            className="absolute inset-0 flex items-center justify-center text-white text-center p-6"
            data-testid="ninja-submitted"
          >
            <div>
              <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-500/20 text-emerald-300 mb-3">
                <Check className="h-7 w-7" />
              </div>
              <div className="text-lg font-bold mb-0.5">
                {submitted.already_entered
                  ? t("play.ninja_already_played")
                  : t("play.ninja_submitted")}
              </div>
              <div className="text-4xl font-extrabold mt-1 tabular-nums">
                {submitted.saved_score}
              </div>
              <div className="text-[11px] text-white/60 uppercase tracking-[0.2em]">
                {t("play.ninja_points")}
              </div>
              <p className="mt-3 text-xs text-white/70 max-w-xs mx-auto">
                {t("play.ninja_winners_hint", {
                  count: game.winners_count || 1,
                })}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Live Top 3 leaderboard */}
      <div
        className="qrise-card px-4 py-3"
        data-testid="ninja-leaderboard"
      >
        <div className="flex items-center justify-between mb-2">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-muted-foreground">
            <Trophy className="h-3 w-3" style={{ color: "#d4af37" }} />
            {t("play.ninja_top3")}
          </div>
        </div>
        {leaderboard.length === 0 ? (
          <div className="text-xs text-muted-foreground py-1">
            {t("play.ninja_no_scores")}
          </div>
        ) : (
          <ul className="space-y-1.5">
            {leaderboard.map((row, i) => (
              <li
                key={i}
                className="flex items-center gap-2 text-sm"
                data-testid={`ninja-leader-${i}`}
              >
                <span
                  className="h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-extrabold shrink-0"
                  style={{
                    background:
                      i === 0
                        ? "linear-gradient(180deg,#f5e4bf,#d4af37,#a98027)"
                        : i === 1
                          ? "linear-gradient(180deg,#e5e7eb,#9ca3af,#6b7280)"
                          : i === 2
                            ? "linear-gradient(180deg,#fcd7b6,#d08a4e,#9a5a2a)"
                            : "transparent",
                    color: "#1a1410",
                  }}
                >
                  {i + 1}
                </span>
                <span className="flex-1 truncate font-medium">
                  {row.first_name}
                </span>
                <span className="font-bold tabular-nums">{row.score}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
